import { afterRenderEffect, Component, computed, ElementRef, inject, input, output, signal } from '@angular/core';
import { DmButton, DmEmptyState, DmIcon } from '@digitale-menschen/component-library-angular';
import { injectMediaQueryMatch } from '../../functions/inject-media-query-match.function';

/** What the state reports. */
export type EmptyStateKind = 'nothing-yet' | 'filtered' | 'load-failed' | 'unavailable';

/**
 * Where the state stands: in place of a list's rows or cards, as the whole body of a drawer, a
 * drawer tab or a modal, or as one section among others that may be empty at the same time.
 */
export type EmptyStatePlacement = 'list' | 'panel' | 'section';

/** What narrowed a filtered list to nothing; names the reset and the description. */
export type EmptyStateNarrowing = 'search' | 'filters' | 'search-and-filters';

/**
 * The one empty, filtered, failed and not-available state of both apps, in the shape
 * `ui-patterns.md` § Empty, filtered, failed and not available sets: a featured icon, a heading,
 * a description and at most one action. The kind decides the icon and the action, the placement
 * the density and whether the circle pattern stands behind the icon.
 *
 * `filtered`, `load-failed` and `unavailable` bring their own action and report it through
 * `resetRequested`, `retryRequested` and `backRequested`. A `nothing-yet` state projects its create button, if the user may
 * create here, as `slot="actions"`.
 *
 * From tablet width up, a `list` state fills the page down to the bottom of the window and centres
 * itself there, leaving room for whatever follows it inside the `.dm-page`, so it never makes the
 * page scroll. On a phone it keeps its own height.
 */
@Component({
  selector: 'lp-empty-state',
  imports: [DmButton, DmEmptyState, DmIcon],
  templateUrl: './empty-state.component.html',
  styleUrl: './empty-state.component.scss',
  host: {
    class: 'lp-empty-state',
    '[style.min-block-size.px]': 'fillHeight()',
  },
})
export class EmptyStateComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly kind = input.required<EmptyStateKind>();
  /** Names the items in the plural: «Noch keine Erlasse», «Keine Erlasse gefunden». */
  public readonly heading = input.required<string>();
  /** Replaces the description `filtered` and `load-failed` bring; the other kinds need one. */
  public readonly description = input<string | null>(null);
  /** The glyph of a `nothing-yet` state: the icon the items carry in their navigation or tab. */
  public readonly icon = input<string | null>(null);
  public readonly placement = input<EmptyStatePlacement>('list');
  public readonly narrowedBy = input<EmptyStateNarrowing>('search-and-filters');

  public readonly resetRequested = output<void>();
  public readonly retryRequested = output<void>();
  public readonly backRequested = output<void>();

  /** The height that reaches the bottom of the window; null keeps the state's own height. */
  protected readonly fillHeight = signal<number | null>(null);
  private readonly isTabletOrWider = injectMediaQueryMatch('(min-width: 768px)');

  protected readonly size = computed<'md' | 'sm'>(() => (this.placement() === 'list' ? 'md' : 'sm'));
  /** Sections can stack, and two sets of rings on top of each other read as decoration. */
  protected readonly pattern = computed<'circle' | 'none'>(() => (this.placement() === 'section' ? 'none' : 'circle'));
  protected readonly iconSize = computed<'lg' | 'md'>(() => (this.placement() === 'section' ? 'md' : 'lg'));
  protected readonly resolvedIcon = computed(() => this.icon() ?? this.kindIcons[this.kind()]);
  /** A failure is the only state in a red disc; every other kind shares the gray box. */
  protected readonly iconTheme = computed<'light' | 'modern'>(() => (this.kind() === 'load-failed' ? 'light' : 'modern'));
  protected readonly iconColor = computed<'error' | 'gray'>(() => (this.kind() === 'load-failed' ? 'error' : 'gray'));
  protected readonly resolvedDescription = computed(() => this.description() ?? this.resolveDefaultDescription());
  protected readonly resetLabel = computed(() => this.resetLabels[this.narrowedBy()]);

  /** Re-measures whenever the window or the page around the state changes size. */
  private readonly fillWindowEffect = afterRenderEffect((onCleanup) => {
    const host = this.elementRef.nativeElement;
    const page = host.closest<HTMLElement>('.dm-page');
    if (this.placement() !== 'list' || !this.isTabletOrWider() || !page || typeof ResizeObserver === 'undefined') {
      this.fillHeight.set(null);
      return;
    }

    const measure = (): void => this.fillHeight.set(this.resolveFillHeight(host, page));
    measure();
    const pageObserver = new ResizeObserver(measure);
    pageObserver.observe(page);
    window.addEventListener('resize', measure);
    onCleanup(() => {
      pageObserver.disconnect();
      window.removeEventListener('resize', measure);
    });
  });

  protected readonly retryLabel = $localize`:@@common.erneutVersuchen:Erneut versuchen`;
  protected readonly backLabel = $localize`:@@common.zurUebersicht:Zurück zur Übersicht`;
  private readonly kindIcons: Record<EmptyStateKind, string> = {
    'nothing-yet': 'file-04',
    filtered: 'search-lg',
    'load-failed': 'alert-circle',
    unavailable: 'alert-circle',
  };
  private readonly resetLabels: Record<EmptyStateNarrowing, string> = {
    search: $localize`:@@common.sucheZuruecksetzen:Suche zurücksetzen`,
    filters: $localize`:@@common.filterZuruecksetzen:Filter zurücksetzen`,
    'search-and-filters': $localize`:@@common.sucheUndFilterZuruecksetzen:Suche und Filter zurücksetzen`,
  };
  private readonly filteredDescriptions: Record<EmptyStateNarrowing, string> = {
    search: $localize`:@@common.emptyState.filtered.search:Ihre Suche ergab keine Treffer.`,
    filters: $localize`:@@common.emptyState.filtered.filters:Ihre Filter ergaben keine Treffer.`,
    'search-and-filters': $localize`:@@common.emptyState.filtered.searchAndFilters:Ihre Suche oder Ihre Filter ergaben keine Treffer.`,
  };

  /**
   * The height from the state's top to the bottom of the window, less what still follows it in
   * the page — the card's border and footer, the page's bottom padding — so the page stays exactly
   * one window high.
   *
   * @param {HTMLElement} host - This component's element
   * @param {HTMLElement} page - The `.dm-page` around it
   * @returns {number | null} The height to fill, or null where the state's own height is taller
   */
  private resolveFillHeight(host: HTMLElement, page: HTMLElement): number | null {
    const hostRect = host.getBoundingClientRect();
    const followingHeight = page.getBoundingClientRect().bottom - hostRect.bottom;
    const available = Math.floor(window.innerHeight - (hostRect.top + window.scrollY) - followingHeight);
    const ownHeight = host.firstElementChild?.getBoundingClientRect().height ?? hostRect.height;
    return available > ownHeight ? available : null;
  }

  private resolveDefaultDescription(): string {
    switch (this.kind()) {
      case 'filtered':
        return this.filteredDescriptions[this.narrowedBy()];
      case 'load-failed':
        return $localize`:@@common.emptyState.loadFailed:Bitte versuchen Sie es erneut.`;
      default:
        return '';
    }
  }
}
